/**
 * Theme Context
 * 
 * Manages UI theme (Modern vs Classic Windows 95/98 style)
 * Persists user preference to localStorage
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UITheme = 'modern' | 'classic';

interface ThemeContextType {
  uiTheme: UITheme;
  setUITheme: (theme: UITheme) => void;
  toggleUITheme: () => void;
  isClassic: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'mars-ui-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [uiTheme, setUIThemeState] = useState<UITheme>(() => {
    // Load theme from localStorage on mount
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored === 'classic' || stored === 'modern') ? stored : 'modern';
  });

  const isClassic = uiTheme === 'classic';

  // Persist theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, uiTheme);
  }, [uiTheme]);

  const setUITheme = (theme: UITheme) => {
    setUIThemeState(theme);
  };

  const toggleUITheme = () => {
    setUIThemeState(prev => prev === 'modern' ? 'classic' : 'modern');
  };

  const value: ThemeContextType = {
    uiTheme,
    setUITheme,
    toggleUITheme,
    isClassic,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}


